import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { wayleaveDocsAPI } from '../utils/api';
import { formatDate } from '../lib/date';

const outcomeColorFor = (outcome) =>
  outcome === 'approved' ? '#059669' : outcome === 'declined' ? '#ef4444' : '#f59e0b';

// Slot status colours (mirrors WayleaveDocuments / spec §4)
const SLOT_STATUS = {
  not_started: { label: 'Not Started', color: '#9ca3af' },
  submitted: { label: 'Awaiting Review', color: '#f59e0b' },
  in_review: { label: 'Under Review', color: '#3b82f6' },
  changes_requested: { label: 'Changes Requested', color: '#f97316' },
  accepted: { label: 'Accepted', color: '#10b981' },
  submitted_to_dre: { label: 'With DRE', color: '#6366f1' },
  dre_query: { label: 'DRE Query', color: '#ef4444' },
  approved: { label: 'Approved', color: '#059669' },
  not_applicable: { label: 'N/A', color: '#9ca3af' },
};
const slotMeta = (s) => SLOT_STATUS[s] || SLOT_STATUS.not_started;

const fmtDate = (d) => formatDate(d);

function StatusChip({ status }) {
  const meta = slotMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
    >
      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

function DecisionForm({ app, onCancel, onDone }) {
  const [mode, setMode] = useState(null); // 'approved' | 'declined' | 'query'
  const [reason, setReason] = useState('');
  const [affected, setAffected] = useState([]);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleAffected = (key) =>
    setAffected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { outcome: mode };
      if (mode === 'declined') payload.reason = reason;
      if (mode === 'query') { payload.reason = reason; payload.affected = affected; }
      if (mode === 'approved') { payload.validFrom = validFrom || null; payload.validTo = validTo || null; }
      await wayleaveDocsAPI.decideSubmission(app.submission.id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => setMode('approved')} className="px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Approve</button>
        <button onClick={() => setMode('query')} className="px-3 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600">Query</button>
        <button onClick={() => setMode('declined')} className="px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700">Decline</button>
        <button onClick={onCancel} className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
      {mode === 'approved' && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Valid from <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Valid to <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
        </div>
      )}

      {(mode === 'declined' || mode === 'query') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={mode === 'query' ? 'What needs to change?' : 'Why is this being declined?'}
          />
        </div>
      )}

      {mode === 'query' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Which documents are affected? <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {app.slots.map((s) => (
              <label key={s.doc_key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={affected.includes(s.doc_key)} onChange={() => toggleAffected(s.doc_key)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                {s.name}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Only the documents you tick are reopened for the contractor; the rest stay locked.</p>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={saving || ((mode === 'declined' || mode === 'query') && !reason.trim()) || (mode === 'query' && affected.length === 0)}
          className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : `Confirm ${mode === 'approved' ? 'approval' : mode}`}
        </button>
        <button onClick={() => setMode(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Back</button>
      </div>
    </div>
  );
}

function ApplicationCard({ app, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState('');
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [letterError, setLetterError] = useState('');
  const letterInputRef = useRef(null);
  const sub = app.submission;
  const decided = !!sub?.outcome;
  const approved = sub?.outcome === 'approved';

  const hasFiles = (app.slots || []).some((s) => s.fileUrl);

  const handleAttachLetter = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setUploadingLetter(true);
    setLetterError('');
    try {
      await wayleaveDocsAPI.uploadApprovalLetter(sub.id, file);
      onChanged();
    } catch (err) {
      setLetterError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploadingLetter(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    setDlError('');
    try {
      await wayleaveDocsAPI.downloadPackDocuments(app.id, app.station_no);
    } catch (err) {
      setDlError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const outcomeColor = sub?.outcome === 'approved' ? '#059669' : sub?.outcome === 'declined' ? '#ef4444' : '#f59e0b';

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Station {app.station_no}</h3>
            {app.owner && <span className="text-xs text-gray-500">{app.owner}</span>}
            {decided && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize" style={{ backgroundColor: `${outcomeColor}1a`, color: outcomeColor }}>
                {sub.outcome}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Submitted {fmtDate(sub?.submitted_at)} by {sub?.submitted_by || '—'}
            {sub?.dre_ref ? ` · Ref ${sub.dre_ref}` : ''}
          </p>
          {decided && (
            <p className="text-xs mt-1" style={{ color: outcomeColor }}>
              {sub.outcome === 'approved'
                ? `Approved${sub.valid_from ? ` · valid ${fmtDate(sub.valid_from)}${sub.valid_to ? ` – ${fmtDate(sub.valid_to)}` : ''}` : ''}`
                : `${sub.outcome === 'query' ? 'Queried' : 'Declined'}${sub.outcome_reason ? ` — ${sub.outcome_reason}` : ''}`}
              {sub.decided_by ? ` (by ${sub.decided_by})` : ''}
            </p>
          )}
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 font-medium">
          {expanded ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
          {expanded ? 'Hide documents' : 'View documents'}
        </button>
      </div>

      {approved && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              <span className="font-medium text-gray-700">Approval Letter</span>
              {sub.approval_letter_url ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  Attached by {sub.approval_letter_uploaded_by || '—'}
                  {sub.approval_letter_uploaded_at ? ` on ${fmtDate(sub.approval_letter_uploaded_at)}` : ''}
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-0.5">Not attached yet — upload the signed letter (optional).</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {sub.approval_letter_url && (
                <a
                  href={sub.approval_letter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View approval letter
                </a>
              )}
              <input
                ref={letterInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={handleAttachLetter}
              />
              <button
                onClick={() => letterInputRef.current?.click()}
                disabled={uploadingLetter}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingLetter ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L7 8m4-4v12" />
                  </svg>
                )}
                {uploadingLetter ? 'Uploading…' : sub.approval_letter_url ? 'Replace' : 'Attach Approval Letter'}
              </button>
            </div>
          </div>
          {letterError && <p className="text-xs text-red-600 mt-1">{letterError}</p>}
        </div>
      )}

      {expanded && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500">
              {app.slots.length} document{app.slots.length === 1 ? '' : 's'}
            </span>
            <div className="flex flex-col items-end">
              <button
                onClick={handleDownloadAll}
                disabled={!hasFiles || downloading}
                title={hasFiles ? 'Download all documents as a ZIP' : 'No uploaded files to download'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                {downloading ? 'Preparing…' : 'Download all'}
              </button>
              {dlError && <span className="text-[11px] text-red-600 mt-1">{dlError}</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rev</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {app.slots.map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-3 py-2"><StatusChip status={s.status} /></td>
                    <td className="px-3 py-2 text-sm text-gray-600">{s.revision_no != null ? `R${s.revision_no}` : '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      {s.fileUrl
                        ? <a href={s.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800">View</a>
                        : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!decided && (
        deciding
          ? <DecisionForm app={app} onCancel={() => setDeciding(false)} onDone={() => { setDeciding(false); onChanged(); }} />
          : <button onClick={() => setDeciding(true)} className="mt-3 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700">Record decision</button>
      )}
    </div>
  );
}

const PAGE_SIZE = 6;

function PaginatedSection({ title, apps, emptyMessage, onChanged }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(apps.length / PAGE_SIZE));

  // Keep the current page valid when the filtered list shrinks.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * PAGE_SIZE;
  const visible = apps.slice(start, start + PAGE_SIZE);

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{title} ({apps.length})</h3>
      {apps.length === 0 ? (
        <div className="card text-center py-8 text-gray-400 text-sm">{emptyMessage}</div>
      ) : (
        <>
          <div className="space-y-3">
            {visible.map((a) => <ApplicationCard key={a.id} app={a} onChanged={onChanged} />)}
          </div>
          {apps.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-4 mt-3 text-sm text-gray-600">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹ Prev
              </button>
              <span className="tabular-nums">
                {start + 1}–{Math.min(start + PAGE_SIZE, apps.length)} of {apps.length}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WayleaveDre() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [mapOpen, setMapOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await wayleaveDocsAPI.getSubmissions();
      setApps(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Allow Escape to close the map pop-up
  useEffect(() => {
    if (!mapOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMapOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapOpen]);

  const filtered = apps.filter((a) => {
    // Search by station no or name (case-insensitive substring)
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${a.station_no ?? ''} ${a.station_name ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Filter by submitted-date range (inclusive)
    const submittedAt = a.submission?.submitted_at ? new Date(a.submission.submitted_at) : null;
    if (fromDate) {
      const from = new Date(`${fromDate}T00:00:00`);
      if (!submittedAt || submittedAt < from) return false;
    }
    if (toDate) {
      const to = new Date(`${toDate}T23:59:59.999`);
      if (!submittedAt || submittedAt > to) return false;
    }
    return true;
  });

  const pending = filtered.filter((a) => !a.submission?.outcome);
  const decided = filtered.filter((a) => a.submission?.outcome);

  const coordApps = filtered.filter((a) => a.lat != null && a.lng != null);
  const hasCoords = apps.some((a) => a.lat != null && a.lng != null);
  const mapCenter = coordApps.length
    ? [coordApps.reduce((s, a) => s + a.lat, 0) / coordApps.length, coordApps.reduce((s, a) => s + a.lng, 0) / coordApps.length]
    : [-33.9, 19.0];

  const hasFilters = search.trim() || fromDate || toDate;
  const resetFilters = () => { setSearch(''); setFromDate(''); setToDate(''); };

  return (
    <div className="w-full">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">DRE Review</h2>
          <p className="text-sm text-gray-600">Wayleave document packs submitted for your decision.</p>
        </div>
        <button
          onClick={() => setMapOpen(true)}
          disabled={!hasCoords}
          title={hasCoords ? 'View submissions on a map' : 'No station coordinates available'}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          View Map
        </button>
      </div>

      {/* Controls: search + submitted-date range */}
      <div className="card mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Station no or name…"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Submitted from</label>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Submitted to</label>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
          {hasFilters && (
            <button onClick={resetFilters} className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Reset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-12 text-gray-500">Loading submissions…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      ) : apps.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">Nothing submitted yet.</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">No submissions match your filters.</div>
      ) : (
        <div className="space-y-6">
          <PaginatedSection title="Awaiting decision" apps={pending} emptyMessage="Nothing waiting on you." onChanged={load} />
          {decided.length > 0 && (
            <PaginatedSection title="Decided" apps={decided} emptyMessage="Nothing decided yet." onChanged={load} />
          )}
        </div>
      )}

      {/* Map pop-up */}
      {mapOpen && (
        <div className="fixed inset-0 z-[9999] bg-white p-3 sm:p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2 px-1">
            <h3 className="font-semibold text-gray-900 text-base">DRE Submissions Map</h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 hidden sm:inline">Press Esc to close</span>
              <button
                onClick={() => setMapOpen(false)}
                className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-sm"
                title="Close map"
              >
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex-1 rounded-lg overflow-hidden border border-gray-200">
            <MapContainer center={mapCenter} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              {coordApps.map((a) => {
                const color = outcomeColorFor(a.submission?.outcome);
                return (
                  <CircleMarker key={a.id} center={[a.lat, a.lng]} radius={8} pathOptions={{ color, fillColor: color, fillOpacity: 0.75, weight: 1 }}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">Station {a.station_no}{a.owner ? ` · ${a.owner}` : ''}</div>
                        <div className="capitalize">{a.submission?.outcome ? `DRE ${a.submission.outcome}` : 'Awaiting decision'}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#059669' }} /> Approved</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#ef4444' }} /> Declined</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f59e0b' }} /> In process</span>
            {coordApps.length === 0 && <span className="text-gray-400">No stations match the current filters.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default WayleaveDre;
